import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import { AppState } from '../../app-state';
import { Project } from '../../features/projects/interfaces/project.interface';
import {
  ActiveDetails,
  Career,
  CareerHeroSection,
  Contact,
  EmailForm,
  Expertise,
  ExpertiseHeroSection,
  News,
} from '../../shared/interfaces/shared-interface';
import * as GlobalActions from './../store/global.actions';

@Injectable()
export class GlobalStoreService {
  constructor(
    private store: Store<AppState>,
    private actions$: Actions
  ) { }

  public sendEmail(emailData: EmailForm): void {
    this.store.dispatch(GlobalActions.sendEmail({ emailData }));
  }

  public sendResume(formData: any): void {
    this.store.dispatch(GlobalActions.sendResume({ formData }));
  }

  public loadingEmail(): Observable<boolean> {
    return this.store.select((s) => s.globalState.emailLoading);
  }

  public fetchAllProjects(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchAllProjects());
      return;
    }

    this.selectAllProjects()
      .pipe(take(1))
      .subscribe((projects) => {
        if (!projects?.length) {
          this.store.dispatch(GlobalActions.fetchAllProjects());
        }
      });
  }

  public selectAllProjects(): Observable<Project[]> {
    return this.store.select((s) => s.globalState.projectsList);
  }

  public fetchUserProfile(): void {
    this.store.dispatch(GlobalActions.fetchProfile());
  }

  public selectUserProfile(): Observable<any> {
    return this.store.select((s) => s.globalState.profile);
  }

  public fetchAllExpertises(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchAllExpertises());
      return;
    }

    this.selectAllExpertises()
      .pipe(take(1))
      .subscribe((expertises) => {
        if (!expertises?.length) {
          this.store.dispatch(GlobalActions.fetchAllExpertises());
        }
      });
  }

  public selectAllExpertises(): Observable<Expertise[]> {
    return this.store.select((s) => s.globalState.expertisesList);
  }

  public fetchActiveDetails(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchActiveDetails());
      return;
    }

    this.selectActiveDetails()
      .pipe(take(1))
      .subscribe((activeDetails) => {
        const hasAnyDetails =
          !!activeDetails?.videoHome ||
          !!activeDetails?.locationList?.length ||
          !!activeDetails?.typeList?.length ||
          !!activeDetails?.expertiseList?.length ||
          !!activeDetails?.categoryList?.length ||
          !!activeDetails?.departmentList?.length;

        if (!hasAnyDetails) {
          this.store.dispatch(GlobalActions.fetchActiveDetails());
        }
      });
  }

  public selectActiveDetails(): Observable<ActiveDetails> {
    return this.store.select((s) => s.globalState.activeDetails);
  }

  public fetchHomeHero(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchHomeHero());
      return;
    }

    this.selectHomeHeroSection()
      .pipe(take(1))
      .subscribe((heroSection) => {
        if (!heroSection) {
          this.store.dispatch(GlobalActions.fetchHomeHero());
        }
      });
  }

  public selectHomeHeroSection(): Observable<any> {
    return this.store.select((s) => s.globalState.homeHeroSection);
  }

  public deleteMedia(existMedia: string): void {
    this.store.dispatch(GlobalActions.deleteMedia({ existMedia }));
  }

  public fetchAllNews(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchAllNews());
      return;
    }

    this.selectAllNews()
      .pipe(take(1))
      .subscribe((news) => {
        if (!news?.length) {
          this.store.dispatch(GlobalActions.fetchAllNews());
        }
      });
  }

  public selectAllNews(): Observable<News[]> {
    return this.store.select((s) => s.globalState.newsList);
  }

  public fetchAllCareers(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchAllCareers());
      return;
    }

    this.selectAllCareers()
      .pipe(take(1))
      .subscribe((careers) => {
        if (!careers?.length) {
          this.store.dispatch(GlobalActions.fetchAllCareers());
        }
      });
  }

  public selectAllCareers(): Observable<Career[]> {
    return this.store.select((s) => s.globalState.careers);
  }

  public selectCareerHeroSection(): Observable<CareerHeroSection | null> {
    return this.store.select((s) => s.globalState.careerHeroSection);
  }

  public selectExpertiseHeroSection(): Observable<ExpertiseHeroSection | null> {
    return this.store.select((s) => s.globalState.expertiseHeroSection);
  }

  public fetchExpertiseHero(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchExpertiseHero());
      return;
    }

    this.selectExpertiseHeroSection()
      .pipe(take(1))
      .subscribe((heroSection) => {
        if (!heroSection) {
          this.store.dispatch(GlobalActions.fetchExpertiseHero());
        }
      });
  }

  public selectExpertisePageTitle(): Observable<string | null> {
    return this.store.select((s) => s.globalState.expertisePageTitle);
  }

  public selectExpertisePageDescription(): Observable<string | null> {
    return this.store.select((s) => s.globalState.expertisePageDescription);
  }

  public deleteDetails(detailsId: number, nameList: string): void {
    this.store.dispatch(GlobalActions.deleteDetails({ detailsId, nameList }));
  }

  public createDetails(details: any): void {
    this.store.dispatch(GlobalActions.createDetails({ details }));
  }

  public selectLoading(): Observable<boolean> {
    return this.store.select((s) => s.globalState.isLoading);
  }

  public fetchStory(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchStory());
      return;
    }

    this.selectStory()
      .pipe(take(1))
      .subscribe((story) => {
        const hasStory = Array.isArray(story) ? story.length > 0 : !!story?.id;
        if (!hasStory) {
          this.store.dispatch(GlobalActions.fetchStory());
        }
      });
  }

  public selectStory(): Observable<any> {
    return this.store.select((s) => s.globalState.story);
  }

  public fetchLeadership(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchLeadership());
      return;
    }

    this.selectLeadership()
      .pipe(take(1))
      .subscribe((leadership) => {
        const hasLeadership = Array.isArray(leadership)
          ? leadership.length > 0
          : !!leadership?.id || !!leadership?.leadershipPartList?.length;

        if (!hasLeadership) {
          this.store.dispatch(GlobalActions.fetchLeadership());
        }
      });
  }

  public selectLeadership(): Observable<any> {
    return this.store.select((s) => s.globalState.leadership);
  }

  public fetchPolicy(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchPolicy());
      return;
    }

    this.selectPolicy()
      .pipe(take(1))
      .subscribe((policy) => {
        const hasPolicy = Array.isArray(policy)
          ? policy.length > 0
          : !!policy?.id || !!policy?.policyPartList?.length;

        if (!hasPolicy) {
          this.store.dispatch(GlobalActions.fetchPolicy());
        }
      });
  }

  public selectPolicy(): Observable<any> {
    return this.store.select((s) => s.globalState.policy);
  }

  public fetchAccreditation(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchAccreditation());
      return;
    }

    this.selectAccreditation()
      .pipe(take(1))
      .subscribe((accreditation) => {
        const hasAccreditation = Array.isArray(accreditation)
          ? accreditation.length > 0
          : !!accreditation?.id || !!accreditation?.accreditationPartList?.length;

        if (!hasAccreditation) {
          this.store.dispatch(GlobalActions.fetchAccreditation());
        }
      });
  }

  public selectAccreditation(): Observable<any> {
    return this.store.select((s) => s.globalState.accreditation);
  }

  public fetchContacts(force = false): void {
    if (force) {
      this.store.dispatch(GlobalActions.fetchContacts());
      return;
    }

    this.selectContacts()
      .pipe(take(1))
      .subscribe((contacts) => {
        if (!contacts?.length) {
          this.store.dispatch(GlobalActions.fetchContacts());
        }
      });
  }

  public selectContacts(): Observable<Contact[]> {
    return this.store.select((s) => s.globalState.contactsList);
  }

  public selectProfileError(): Observable<any> {
    return this.store.select((s) => s.globalState.error);
  }

  public loadVideo(fileName: string): void {
    this.store.dispatch(GlobalActions.loadVideo({ fileName }));
  }

  public selectLoadVideo(): Observable<any> {
    return this.store.select((s) => s.globalState.video);
  }

  public selectUpdateRequired(): Observable<any> {
    return this.actions$.pipe(ofType(GlobalActions.loadVideoSuccess));
  }
}
